import { FormEvent, useState } from "react";
import { MyFeedbackItem } from "../../../types";
import { FeedbackApi } from "../api";
import { readFeedbackContext } from "../feedbackContext";

// "Your feedback": fetched when opened, plus a manual refresh. No polling.
const MyFeedback = () => {
  const [items, setItems] = useState<MyFeedbackItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await FeedbackApi.getMine();
      if (res.success && res.data) setItems(res.data.feedback);
      else setFailed(true);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  };

  return (
    <details
      className="text-xs"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && items === null && !loading) load();
      }}
    >
      <summary className="cursor-pointer py-2 sm:py-0 text-neutral-400">Your feedback</summary>
      <div className="mt-1 space-y-1">
        <button type="button" onClick={load} disabled={loading} className="min-h-10 sm:min-h-0 text-neutral-400 underline">
          {loading ? "Loading…" : "Refresh"}
        </button>
        {failed && <p className="text-neutral-500">Couldn't load your feedback.</p>}
        {items?.length === 0 && <p className="text-neutral-500">Nothing sent yet.</p>}
        <ul className="max-h-40 overflow-y-auto space-y-1">
          {items?.map((i) => (
            <li key={i.id} className="flex justify-between gap-2">
              <span className="truncate text-neutral-700" title={i.text}>
                {i.text}
              </span>
              <span className="shrink-0 text-neutral-500">{i.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
};

export const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    await FeedbackApi.create({
      id: crypto.randomUUID(),
      text: text.trim(),
      page: window.location.pathname,
      context: readFeedbackContext(),
    });
    setText("");
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setOpen(false);
    }, 1200);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] min-h-10 sm:min-h-0 text-xs bg-neutral-900 text-white rounded-full px-4 py-2 shadow"
      >
        Feedback
      </button>
    );
  }

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] w-72 max-w-[calc(100vw-2rem-env(safe-area-inset-right))] max-h-[80vh] overflow-y-auto bg-white border rounded shadow p-3 space-y-2">
      {sent ? (
        <p className="text-sm text-neutral-500">Thanks!</p>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <textarea
            className="w-full border rounded px-2 py-1 text-sm"
            rows={3}
            placeholder="Something broken, missing, or annoying?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="min-h-10 sm:min-h-0 px-2 text-xs text-neutral-400"
            >
              Cancel
            </button>
            <button className="min-h-10 sm:min-h-0 text-xs bg-neutral-900 text-white rounded px-4 sm:px-3 py-1">
              Send
            </button>
          </div>
        </form>
      )}
      {!sent && <MyFeedback />}
    </div>
  );
};
